import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import Select from "react-select";
import { useHistory } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Form,
  Pagination,
  PaginationItem,
  Collapse,
  PaginationLink,
  Input,
} from "reactstrap";
import TicketListRow from "./TicketList";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import jwt_decode from "jwt-decode";
import axios from "../api/axios";

const CUSTOMER_SEARCH_URL = "/ticket/filter_tickets_customer";
const MANAGER_SEARCH_URL = "/ticket/filter_tickets_manager";
const COMPANY_SEARCH_URL = "/ticket/filter_tickets_company";
const EXCEL_DOWNLOAD_URL = "/ticket/download_excel";

const PAGE_LIMIT = 10;

const STATUS_OPTIONS = ["Open", "Scheduled", "Inprogress", "Completed", "Unresolved"].map((s) => ({
  value: s,
  label: s,
}));

// Assignment filter: values are understood by the server's buildAssignmentClause.
const ASSIGNMENT_OPTIONS = [
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
  { value: "technicalStaff", label: "Maintenance Team" },
  { value: "vendor", label: "External Vendors" },
];

/** One object holds every filter control. Selects keep the react-select option, inputs keep strings. */
const EMPTY_FILTERS = {
  startDate: "",
  endDate: "",
  ticketID: "",
  status: "",
  assignment: "",
  property: null,
  propertyManager: null,
  vendor: null,
  technicalStaff: null,
  requestType: null,
  // Optional sub-type of the request type ("Fridge" under "Appliance").
  requestSubType: null,
  suite: "",
};

const BROWSER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    return "";
  }
})();

const personLabel = (p) =>
  [p?.first_name ?? p?.firstname, p?.last_name ?? p?.lastname].filter(Boolean).join(" ") ||
  p?.email ||
  "";

const TicketList = () => {
  const decode = jwt_decode(window.localStorage.getItem("accessToken"));
  const userRole = JSON.parse(window.localStorage.getItem("authUser"))?.role || decode.role;

  // Auditors are scoped to a company but their token id is their own auditor id,
  // so use company_id when a company-level id is expected.
  const companyScopeId = decode.role === "auditor" ? decode.company_id : decode.id;
  const isCompanyScope = !["customer", "manager"].includes(decode.role);

  const history = useHistory();

  const [col5, setcol5] = useState(false);
  const t_col5 = () => setcol5(!col5);

  // `draft` is what the panel shows; `applied` is what the list is actually
  // filtered by (only changes on Search / Clear). Keeping them separate means
  // typing in the panel never fires requests and pagination always uses the
  // filters that produced the current result set.
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(1);
  const [startPage, setStartPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [filteredTickets, setFilteredTickets] = useState([]);
  const [notesCount, setNotesCount] = useState({});
  const [totalTickets, setTotalTickets] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    scheduled: 0,
    inprogress: 0,
    completed: 0,
    unresolved: 0,
    timetaken: 0,
  });

  // Dropdown data
  const [ticketPropertyList, setTicketPropertyList] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  // Every (requestType, requestSubType) pair that occurs in the viewer's scope,
  // from the server — the Sub-type filter offers only the chosen type's ones.
  const [requestSubTypes, setRequestSubTypes] = useState([]);
  const [propertyManagerList, setPropertyManagerList] = useState([]);
  const [comapnyTechnicalStaff, setComapnyTechnicalStaff] = useState([]);
  const [comapnyVendors, setComapnyVendors] = useState([]);

  const [url, setUrl] = useState("");
  const [copied, setcopied] = useState(false);

  const pageTotal = Math.max(1, Math.ceil(stats.total / PAGE_LIMIT));

  const setDraftField = (field) => (value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const handlePageChange = (page) => {
    if (page < 1 || page > pageTotal || page === pageNumber) return;
    setPageNumber(page);
    if (page > startPage + PAGE_LIMIT - 1 || page < startPage) {
      setStartPage(Math.max(1, page - Math.floor(PAGE_LIMIT / 2)));
    }
  };

  /** Turn the applied filters into the request body every list/export endpoint understands. */
  const buildFilterBody = useCallback(
    (filters) => {
      const body = {
        startDate: filters.startDate || "",
        endDate: filters.endDate || "",
        timezone: BROWSER_TIMEZONE,
        status: filters.status || "",
        assignment: filters.assignment || "",
        propertyID: filters.property?.value || "",
        ticketID: (filters.ticketID || "").trim(),
        suite: (filters.suite || "").trim(),
        requestType: filters.requestType?.value || "",
        requestSubType: filters.requestSubType?.value || "",
      };
      if (decode.role === "customer") {
        body.customerID = decode.id;
      } else if (decode.role === "manager") {
        body.managerID = decode.id;
      } else {
        body.companyDomain = decode.domain;
        body.propertyManagerID = filters.propertyManager?.value || "";
        body.vendorID = filters.vendor?.value || "";
        body.technicalStaffID = filters.technicalStaff?.value || "";
      }
      return body;
    },
    [decode.role, decode.id, decode.domain]
  );

  const searchUrl =
    decode.role === "customer"
      ? CUSTOMER_SEARCH_URL
      : decode.role === "manager"
      ? MANAGER_SEARCH_URL
      : COMPANY_SEARCH_URL;

  // The one and only place the list is fetched. Runs whenever the applied
  // filters, the page, or the reload key change. Out-of-order responses are
  // dropped so a slow earlier request can never overwrite a newer result.
  const latestRequest = useRef(0);
  useEffect(() => {
    const requestId = ++latestRequest.current;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const response = await axios.post(searchUrl, {
          ...buildFilterBody(applied),
          pageNumber,
        });
        if (cancelled || requestId !== latestRequest.current) return;

        const data = response.data || {};
        if (response.status !== 200 || data.status === 500 || data.success === false) {
          throw new Error(data.message || "Failed to load maintenance requests");
        }

        const ticketsArray = data.tickets || data.results?.tickets || [];
        setFilteredTickets(ticketsArray);
        setNotesCount(data.notesCount || {});
        setTotalTickets(data.totalCount || data.total?.Total || 0);
        if (Array.isArray(data.propertyList)) setTicketPropertyList(data.propertyList);
        if (Array.isArray(data.requestTypes)) setRequestTypes(data.requestTypes);
        if (Array.isArray(data.requestSubTypes)) setRequestSubTypes(data.requestSubTypes);
        setStats({
          total: data.total?.Total || data.totalCount || 0,
          open: data.total?.Open?.count || 0,
          scheduled: data.total?.Scheduled?.count || 0,
          completed: data.total?.Completed?.count || 0,
          inprogress: data.total?.Inprogress?.count || 0,
          unresolved: data.total?.Unresolved?.count || 0,
          timetaken: data.total?.TotalTime || 0,
        });
      } catch (error) {
        if (cancelled || requestId !== latestRequest.current) return;
        console.error("Error searching tickets:", error);
        setLoadError(error?.response?.data?.message || error.message || "Failed to load maintenance requests");
      } finally {
        if (!cancelled && requestId === latestRequest.current) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [applied, pageNumber, reloadKey, searchUrl, buildFilterBody]);

  /** Re-run the current search (same filters, same page). Used by row actions after they mutate a ticket. */
  const refreshTickets = useCallback(() => setReloadKey((k) => k + 1), []);

  const applyFilters = () => {
    if (draft.startDate && draft.endDate && draft.startDate > draft.endDate) {
      // Normalise a reversed range instead of silently returning nothing.
      const swapped = { ...draft, startDate: draft.endDate, endDate: draft.startDate };
      setDraft(swapped);
      setApplied(swapped);
    } else {
      setApplied({ ...draft });
    }
    setPageNumber(1);
    setStartPage(1);
  };

  const ClearTicket = (e) => {
    e?.preventDefault?.();
    setDraft(EMPTY_FILTERS);
    setApplied({ ...EMPTY_FILTERS });
    setPageNumber(1);
    setStartPage(1);
    setcol5(false);
  };

  // Static dropdown data + share link. Independent of the list, so it loads in parallel.
  useEffect(() => {
    let active = true;
    const safe = (fn) => (res) => {
      if (active) fn(res);
    };
    const swallow = (label) => (err) => console.error(`Error loading ${label}:`, err);

    if (isCompanyScope) {
      axios
        .get("/vendor/get_vendors/" + companyScopeId)
        .then(safe((res) => res.data?.success && setComapnyVendors(res.data.vendors || [])))
        .catch(swallow("vendors"));
      axios
        .get("/technicalStaff/get_technical_staff/" + companyScopeId)
        .then(safe((res) => res.data?.success && setComapnyTechnicalStaff(res.data.technicalStaff || [])))
        .catch(swallow("technical staff"));
    }
    if (decode.role === "company") {
      axios
        .post(`/property_manager/admin`, { company: decode.id })
        .then(safe((res) => res.data?.managers && setPropertyManagerList(res.data.managers)))
        .catch(swallow("property managers"));
    }
    axios
      .get(`company/get-url/?company_id=${companyScopeId}&role=${decode.role}`)
      .then(
        safe((res) => {
          if (res.data?.status === 200) {
            setUrl(`${window.location.protocol}//${window.location.host}/maintenance-request/${res.data.data}`);
          }
        })
      )
      .catch(swallow("maintenance link"));

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadExcel = async () => {
    try {
      // Export exactly what the list is currently filtered by.
      const query = {
        ...buildFilterBody(applied),
        role: decode.role,
        id: decode.role === "auditor" ? decode.company_id : decode.id,
      };

      const response = await axios.post(EXCEL_DOWNLOAD_URL, query, {
        responseType: "blob",
        headers: {
          Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      if (blob.size < 100) throw new Error("Empty or invalid file");

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      link.setAttribute("download", `tickets_${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      link.remove();
    } catch (error) {
      console.error("Excel download failed:", error);
    }
  };

  const formatTimeTaken = (totalMinutes) => {
    if (!totalMinutes) return "0 min";
    const years = Math.floor(totalMinutes / (365.25 * 24 * 60));
    const months = Math.floor((totalMinutes % (365.25 * 24 * 60)) / (30.44 * 24 * 60));
    const days = Math.floor((totalMinutes % (30.44 * 24 * 60)) / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = Math.round(totalMinutes % 60);

    const parts = [];
    if (years > 0) parts.push(`${years} yr`);
    if (months > 0) parts.push(`${months} mo`);
    if (days > 0) parts.push(`${days} d`);
    if (hours > 0) parts.push(`${hours} hr`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes} min`);

    return parts.join(", ");
  };

  const propertyOptions = useMemo(
    () => ticketPropertyList.map((p) => ({ value: p._id, label: p.title })),
    [ticketPropertyList]
  );
  const requestTypeOptions = useMemo(() => requestTypes.map((t) => ({ value: t, label: t })), [requestTypes]);
  // With a request type chosen: that type's sub-types. Without one: every
  // sub-type in scope, labelled with its type so "Fridge" under two different
  // types cannot be confused. Empty → the Sub-type filter is not rendered.
  const requestSubTypeOptions = useMemo(() => {
    const chosenType = draft.requestType?.value || "";
    const pairs = chosenType
      ? requestSubTypes.filter((p) => p.requestType === chosenType)
      : requestSubTypes;
    const seen = new Set();
    return pairs
      .filter((p) => p.requestSubType && !seen.has(p.requestSubType) && seen.add(p.requestSubType))
      .map((p) => ({
        value: p.requestSubType,
        label: chosenType ? p.requestSubType : `${p.requestType} — ${p.requestSubType}`,
      }));
  }, [requestSubTypes, draft.requestType]);
  const managerOptions = useMemo(
    () => propertyManagerList.map((m) => ({ value: m._id, label: personLabel(m) })),
    [propertyManagerList]
  );
  const vendorOptions = useMemo(
    () =>
      comapnyVendors.map((v) => ({
        value: v._id,
        label: v.agency_name ? `${personLabel(v)} (${v.agency_name})` : personLabel(v),
      })),
    [comapnyVendors]
  );
  const staffOptions = useMemo(
    () => comapnyTechnicalStaff.map((t) => ({ value: t._id, label: personLabel(t) })),
    [comapnyTechnicalStaff]
  );

  const activeFilterCount = useMemo(
    () =>
      Object.values(applied).filter((v) => (typeof v === "string" ? v.trim() !== "" : v != null)).length,
    [applied]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | Maintenance Request</title>
        </MetaTags>

        <div className="col-md-12 mb-3">
          {!["vendor", "technical staff", "manager", "customer"].includes(decode.role) && (
            <CopyToClipboard text={url} onCopy={() => setcopied(true)}>
              <button
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(url);
                }}
              >
                Copy Maintenance Link
              </button>
            </CopyToClipboard>
          )}
          {copied && (
            <span className="ms-2 text-success">Copied!</span>
          )}
        </div>

        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Maintenance Request" />

          <Row>
            <Col xl={12}>
              <Card>
                <CardHeader>
                  <div className="row align-items-center">
                    <div className="col-md-6 d-flex flex-wrap">
                      {["Total", "Open", "Scheduled", "Inprogress", "Completed", "Unresolved"].map((key) => (
                        <div key={key} className="mx-2 mb-2">
                          <h5 className="card-title mb-0">
                            {key}
                            <span className="text-muted fw-normal ms-1">
                              ({stats[key.toLowerCase()] || 0})
                            </span>
                          </h5>
                        </div>
                      ))}
                      <div className="mx-2 mb-2">
                        <h5 className="card-title mb-0" style={{ minWidth: "32rem" }}>
                          Time Taken
                          <span className="text-muted fw-normal ms-1">
                            {formatTimeTaken(stats.timetaken)}
                          </span>
                        </h5>
                      </div>
                    </div>

                    <div className="col-md-6 d-flex flex-wrap align-items-center justify-content-end gap-2">
                      {decode.role !== "customer" && (
                        <button onClick={downloadExcel} className="btn btn-primary">
                          Export Excel
                        </button>
                      )}
                      <button onClick={t_col5} className="btn btn-primary">
                        Filters
                        {activeFilterCount > 0 && (
                          <span className="badge bg-light text-dark ms-2">{activeFilterCount}</span>
                        )}
                      </button>
                      {(decode.role === "manager" || decode.role === "customer") && (
                        <button
                          onClick={() => history.push("/ticket_manager")}
                          className="btn btn-light"
                        >
                          <i className="bx bx-plus me-1"></i>Add Maintenance Request
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-lg-12">
                      <Collapse isOpen={col5}>
                        <Card>
                          <CardBody>
                            <Form
                              onSubmit={(e) => {
                                e.preventDefault();
                                applyFilters();
                              }}
                            >
                              <div className="row align-items-end">
                                <div className="col-md-3">
                                  <Label>From</Label>
                                  <Input
                                    type="date"
                                    value={draft.startDate}
                                    max={draft.endDate || undefined}
                                    onChange={(e) => setDraftField("startDate")(e.target.value)}
                                  />
                                </div>
                                <div className="col-md-3">
                                  <Label>To</Label>
                                  <Input
                                    type="date"
                                    value={draft.endDate}
                                    min={draft.startDate || undefined}
                                    onChange={(e) => setDraftField("endDate")(e.target.value)}
                                  />
                                </div>
                                <div className="col-md-3">
                                  <Label>Request ID</Label>
                                  <Input
                                    type="text"
                                    value={draft.ticketID}
                                    onChange={(e) => setDraftField("ticketID")(e.target.value)}
                                    placeholder="e.g. TIK1234"
                                  />
                                </div>
                                <div className="col-md-3">
                                  <Label>Status</Label>
                                  <Select
                                    value={STATUS_OPTIONS.find((o) => o.value === draft.status) || null}
                                    onChange={(opt) => setDraftField("status")(opt ? opt.value : "")}
                                    options={STATUS_OPTIONS}
                                    isClearable
                                    placeholder="Select..."
                                  />
                                </div>
                                {decode.role !== "customer" && (
                                  <div className="col-md-3">
                                    <Label>Assignment</Label>
                                    <Select
                                      value={ASSIGNMENT_OPTIONS.find((o) => o.value === draft.assignment) || null}
                                      onChange={(opt) => setDraftField("assignment")(opt ? opt.value : "")}
                                      options={ASSIGNMENT_OPTIONS}
                                      isClearable
                                      placeholder="Select..."
                                    />
                                  </div>
                                )}
                                <div className="col-md-3">
                                  <Label>Property</Label>
                                  <Select
                                    value={draft.property}
                                    onChange={setDraftField("property")}
                                    options={propertyOptions}
                                    isClearable
                                    placeholder="Select property"
                                  />
                                </div>

                                {userRole === "company" && (
                                  <>
                                    <div className="col-md-3">
                                      <Label>Property Manager</Label>
                                      <Select
                                        value={draft.propertyManager}
                                        onChange={setDraftField("propertyManager")}
                                        options={managerOptions}
                                        isClearable
                                        placeholder="Select..."
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <Label>Vendors</Label>
                                      <Select
                                        value={draft.vendor}
                                        onChange={setDraftField("vendor")}
                                        options={vendorOptions}
                                        isClearable
                                        placeholder="Select..."
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <Label>Technical Staff</Label>
                                      <Select
                                        value={draft.technicalStaff}
                                        onChange={setDraftField("technicalStaff")}
                                        options={staffOptions}
                                        isClearable
                                        placeholder="Select..."
                                      />
                                    </div>
                                  </>
                                )}

                                <div className="col-md-3">
                                  <Label>Request Type</Label>
                                  <Select
                                    value={draft.requestType}
                                    onChange={(opt) =>
                                      // A different type has different sub-types,
                                      // so a chosen sub-type no longer applies.
                                      setDraft((prev) => ({ ...prev, requestType: opt, requestSubType: null }))
                                    }
                                    options={requestTypeOptions}
                                    isClearable
                                    placeholder="Select..."
                                  />
                                </div>
                                {requestSubTypeOptions.length > 0 && (
                                  <div className="col-md-3">
                                    <Label>
                                      {draft.requestType?.value ? `${draft.requestType.value} Type` : "Sub-type"}
                                    </Label>
                                    <Select
                                      value={draft.requestSubType}
                                      onChange={setDraftField("requestSubType")}
                                      options={requestSubTypeOptions}
                                      isClearable
                                      placeholder="Any"
                                    />
                                  </div>
                                )}
                                <div className="col-md-3">
                                  <Label>Suite</Label>
                                  <Input
                                    type="text"
                                    value={draft.suite}
                                    onChange={(e) => setDraftField("suite")(e.target.value)}
                                    placeholder="Enter suite"
                                  />
                                </div>

                                <div className="col-md-12 text-end mt-3">
                                  <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                                    {loading ? "Searching…" : "Search"}
                                  </button>
                                  <button type="button" className="btn btn-secondary" onClick={ClearTicket}>
                                    Clear
                                  </button>
                                </div>
                              </div>
                            </Form>
                          </CardBody>
                        </Card>
                      </Collapse>
                    </div>
                  </div>
                </CardHeader>

                <CardBody>
                  {loadError && (
                    <div className="alert alert-danger py-2" role="alert">
                      {loadError}
                    </div>
                  )}
                  <div className="table-responsive">
                    <Table className="table-striped table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>ID</th>
                          <th style={{ minWidth: "110px" }}>Suite No.</th>
                          <th>Type</th>
                          {decode.role !== "customer" && (
                            <>
                              <th>Property</th>
                              <th style={{ minWidth: "110px" }}>Submitted By</th>
                            </>
                          )}
                          <th style={{ minWidth: "80px" }}>Created</th>
                          <th style={{ minWidth: "80px" }}>Age</th>
                          <th style={{ minWidth: "50px" }}>Permission</th>
                          <th>Workflow Details</th>
                          <th style={{ minWidth: "50px" }}>Status</th>
                          <th>Download</th>
                          <th>Action</th>
                          {(decode.role === "company" || decode.role === "manager") && <th>Assign To</th>}
                          {decode.role !== "customer" && <th>History</th>}
                          <th>Memo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!loading && filteredTickets.length === 0 && (
                          <tr>
                            <td colSpan={16} className="text-center text-muted py-4">
                              {activeFilterCount > 0
                                ? "No maintenance requests match the selected filters."
                                : "No maintenance requests yet."}
                            </td>
                          </tr>
                        )}
                        <TicketListRow
                          filteredTickets={filteredTickets}
                          notesCount={notesCount}
                          searchTicket={refreshTickets}
                          getTicket={refreshTickets}
                          page={pageNumber}
                          totalTickets={totalTickets}
                          setTotalTickets={setTotalTickets}
                        />
                      </tbody>
                    </Table>
                  </div>

                  <Row className="mt-4">
                    <Col xl={12}>
                      <div className="pagination-bar d-flex justify-content-center">
                        <Pagination>
                          <PaginationItem disabled={pageNumber === 1}>
                            <PaginationLink first onClick={() => handlePageChange(1)} />
                          </PaginationItem>
                          <PaginationItem disabled={pageNumber === 1}>
                            <PaginationLink previous onClick={() => handlePageChange(pageNumber - 1)} />
                          </PaginationItem>
                          {Array.from({ length: PAGE_LIMIT }, (_, i) => startPage + i)
                            .filter((p) => p <= pageTotal)
                            .map((page) => (
                              <PaginationItem key={page} active={pageNumber === page}>
                                <PaginationLink onClick={() => handlePageChange(page)}>
                                  {page}
                                </PaginationLink>
                              </PaginationItem>
                            ))}
                          <PaginationItem disabled={pageNumber >= pageTotal}>
                            <PaginationLink next onClick={() => handlePageChange(pageNumber + 1)} />
                          </PaginationItem>
                          <PaginationItem disabled={pageNumber >= pageTotal}>
                            <PaginationLink last onClick={() => handlePageChange(pageTotal)} />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default TicketList;
